import { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  LayoutDashboard, Package, PawPrint, MapPin, FileText, LifeBuoy, Heart,
  Clock, Settings, LogOut, ShieldAlert, TrendingUp,
} from "lucide-react";
import { FEATURES } from "@/lib/features";

const baseLinks = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/dashboard/orders", label: "Orders", icon: Package },
  ...(FEATURES.pets ? [{ to: "/dashboard/pets", label: "Pets", icon: PawPrint }] : []),
  { to: "/dashboard/addresses", label: "Addresses", icon: MapPin },
  { to: "/dashboard/invoices", label: "Invoices", icon: FileText },
  { to: "/dashboard/tickets", label: "Support", icon: LifeBuoy },
  { to: "/dashboard/wishlist", label: "Wishlist", icon: Heart },
  { to: "/dashboard/recently-viewed", label: "Recently Viewed", icon: Clock },
  { to: "/dashboard/profile", label: "Profile", icon: Settings },
];

export default function DashboardLayout() {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [affiliate, setAffiliate] = useState(null);
  const [affLoaded, setAffLoaded] = useState(false);

  const refreshAffiliate = useCallback(async () => {
    try {
      const r = await api.get("/me/affiliate");
      setAffiliate(r.data);
    } catch {
      setAffiliate({ status: "none", signup_enabled: true });
    } finally {
      setAffLoaded(true);
    }
  }, []);

  useEffect(() => { refreshAffiliate(); }, [refreshAffiliate]);

  const showAffiliateNav = affiliate && ["active", "paused", "pending"].includes(affiliate.status);

  const links = [
    ...baseLinks,
    ...(showAffiliateNav
      ? [{ to: "/dashboard/affiliate", label: "Affiliate", icon: TrendingUp }]
      : []),
  ];

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
      <aside className="lg:sticky lg:top-24 lg:self-start space-y-3">
        <div className="border border-slate-200 rounded-md p-4 bg-white">
          <div className="flex items-center gap-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-10 h-10 rounded-md object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-md bg-blue-600 text-white text-sm font-semibold flex items-center justify-center">
                {user?.name?.[0] || "U"}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">{user?.name}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            </div>
          </div>
          {user && !user.email_verified && (
            <div className="mt-3 flex items-start gap-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2">
              <ShieldAlert className="w-3.5 h-3.5 mt-0.5" strokeWidth={1.75} />
              <span>Email not verified. <a href="/dashboard/profile" className="underline">Verify now</a>.</span>
            </div>
          )}
        </div>

        <nav data-testid="dashboard-sidebar" className="border border-slate-200 rounded-md p-2 bg-white">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`sidebar-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive ? "bg-blue-50 text-blue-700" : "text-slate-700 hover:bg-slate-50"
                }`
              }
            >
              <l.icon className="w-4 h-4" strokeWidth={1.75} /> {l.label}
              {l.label === "Affiliate" && affiliate?.status === "pending" && (
                <span className="ml-auto text-[9px] font-mono uppercase tracking-widest text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">Pending</span>
              )}
            </NavLink>
          ))}
          {isAdmin && (
            <NavLink
              to="/admin"
              className="mt-2 flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-slate-900 bg-slate-100 hover:bg-slate-200"
              data-testid="sidebar-admin-panel"
            >
              <ShieldAlert className="w-4 h-4" strokeWidth={1.75} /> Admin Panel
            </NavLink>
          )}
          <button
            onClick={async () => { await logout(); navigate("/"); }}
            data-testid="sidebar-logout"
            className="mt-2 w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-red-600 hover:bg-red-50"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.75} /> Sign out
          </button>
        </nav>
      </aside>
      <div className="min-w-0"><Outlet context={{ affiliate, affLoaded, refreshAffiliate }} /></div>
    </div>
  );
}
